import type { Pt } from "./cable-routing";

/**
 * Polyline interpolation for the cable "settle" animation. Intermediate frames
 * are deliberately not orthogonal.
 */

export function polylineArcLength(pts: readonly Pt[]): number {
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    total += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
  }
  return total;
}

/** `samples` is floored at 2 and the original endpoints are kept. Empty input
 *  returns `[]`; a single point or a zero-length path repeats that point. */
export function resamplePolyline(pts: readonly Pt[], samples: number): Pt[] {
  const n = Math.max(2, samples);
  if (pts.length === 0) return [];
  const first = pts[0];
  const last = pts[pts.length - 1];
  const total = polylineArcLength(pts);
  if (pts.length === 1 || total === 0) {
    return Array.from({ length: n }, () => ({ ...first }));
  }
  const out: Pt[] = [{ ...first }];
  let segIdx = 0;
  let segStartDist = 0;
  let segLen = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
  for (let i = 1; i < n - 1; i++) {
    const target = (total * i) / (n - 1);
    while (segStartDist + segLen < target && segIdx < pts.length - 2) {
      segStartDist += segLen;
      segIdx++;
      segLen = Math.hypot(
        pts[segIdx + 1].x - pts[segIdx].x,
        pts[segIdx + 1].y - pts[segIdx].y,
      );
    }
    const t = segLen === 0 ? 0 : (target - segStartDist) / segLen;
    out.push({
      x: pts[segIdx].x + (pts[segIdx + 1].x - pts[segIdx].x) * t,
      y: pts[segIdx].y + (pts[segIdx + 1].y - pts[segIdx].y) * t,
    });
  }
  out.push({ ...last });
  return out;
}

export function lerpPolylines(a: readonly Pt[], b: readonly Pt[], t: number): Pt[] {
  const out: Pt[] = new Array(Math.min(a.length, b.length));
  for (let i = 0; i < out.length; i++) {
    out[i] = {
      x: a[i].x + (b[i].x - a[i].x) * t,
      y: a[i].y + (b[i].y - a[i].y) * t,
    };
  }
  return out;
}

export function samePolyline(a: readonly Pt[], b: readonly Pt[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].x !== b[i].x || a[i].y !== b[i].y) return false;
  }
  return true;
}

/** Plain line-to SVG path — resampled points are too dense for corner rounding. */
export function polylinePath(pts: readonly Pt[]): string {
  if (pts.length === 0) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x} ${pts[i].y}`;
  return d;
}
